import { z } from "zod";
import { DATE_PRESETS, RETURN_STATUSES, RETURN_TYPES } from "../enums";
import type { ReturnStatus, ReturnType } from "../enums";

// Returns (Retours): carrier returns (RTO, created by the shipment status sync) and customer returns or exchanges
// created from a delivered order. Receiving restocks the items marked for restock.

const id = z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide");
const minor = z.number().int("Montant invalide").min(0, "Montant invalide");

export const RETURN_REFUND_METHODS = ["cash", "bank_transfer", "card", "wallet", "voucher", "other"] as const;
export type ReturnRefundMethod = (typeof RETURN_REFUND_METHODS)[number];

export const returnListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  q: z.string().trim().max(120).optional(),
  status: z.enum([...RETURN_STATUSES, "all"] as const).default("all"),
  type: z.enum(RETURN_TYPES).optional(),
  orderId: id.optional(),
  preset: z.enum(DATE_PRESETS).optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});
export type ReturnListQuery = z.infer<typeof returnListQuery>;
export type ReturnListQueryInput = z.input<typeof returnListQuery>;

export const returnCreateSchema = z.object({
  orderId: id,
  type: z.enum(["customer_return", "exchange"]),
  reason: z.string().trim().max(500).optional().default(""),
  items: z
    .array(z.object({ itemId: id, qty: z.number().int().min(1, "Quantité minimum 1"), restock: z.boolean().optional().default(true) }))
    .min(1, "Choisissez au moins un article"),
  carrierFeeMinor: minor.optional().default(0),
  notes: z.string().trim().max(2000).optional().default(""),
});
export type ReturnCreateInput = z.infer<typeof returnCreateSchema>;

export const returnUpdateSchema = z.object({
  reason: z.string().trim().max(500).optional(),
  notes: z.string().trim().max(2000).optional(),
  carrierFeeMinor: minor.optional(),
});
export type ReturnUpdateInput = z.infer<typeof returnUpdateSchema>;

export const returnReceiveSchema = z.object({
  /** Restock choice per item (index in the return items). Absent = keep the choice made at creation. */
  items: z.array(z.object({ index: z.number().int().min(0), restock: z.boolean() })).max(100).optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type ReturnReceiveInput = z.infer<typeof returnReceiveSchema>;

export const returnRefundSchema = z.object({
  amountMinor: z.number().int("Montant invalide").min(1, "Montant invalide"),
  method: z.enum(RETURN_REFUND_METHODS),
  status: z.enum(["pending", "done"]),
});
export type ReturnRefundInput = z.infer<typeof returnRefundSchema>;

export interface ReturnDto {
  id: string;
  orderId: string;
  orderNumber: string;
  shipmentId: string | null;
  customerId: string | null;
  customer: { name: string; phone: string };
  type: ReturnType;
  status: ReturnStatus;
  reason: string;
  items: Array<{ productId: string | null; variantId: string | null; name: string; qty: number; restock: boolean }>;
  refund: { amountMinor: number; method: string | null; status: "none" | "pending" | "done"; at: string | null };
  carrierFeeMinor: number;
  notes: string;
  carrierCode: string | null;
  trackingNumber: string | null;
  orderTotalMinor: number;
  currency: string;
  receivedAt: string | null;
  restockedAt: string | null;
  createdBy: { type: string; id: string | null; name: string | null };
  createdAt: string;
  updatedAt: string;
}

export type ReturnCountsDto = Record<ReturnStatus | "all", number>;
